"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StreemWallSync = void 0;
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const minimal_1 = require("protobufjs/minimal");
const wall_service_client_1 = require("../util/wall_service_client");
const logger_1 = require("../util/logger");
class StreemWallSync {
    constructor(wallItemDecoder, opts) {
        this.wallItemDecoder = wallItemDecoder;
        this.opts = opts;
        this.eventSubject = new rxjs_1.Subject();
        this.connectedSubject = new rxjs_1.BehaviorSubject(false);
        this.reconnectFailedSubject = new rxjs_1.BehaviorSubject(false);
        this.isSyncConnected = () => this.connectedSubject.value;
        this.events = this.eventSubject.asObservable();
        this.connected = this.connectedSubject.asObservable();
        this.reconnectFailed = this.reconnectFailedSubject.asObservable();
        this.encodeWallItem = (item) => {
            const base64Buffer = this.wallItemDecoder.encode(item).finish();
            return minimal_1.util.base64.encode(base64Buffer, 0, base64Buffer.length);
        };
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info(`StreemWallSync connecting`);
            this.connectedSubject.next(false);
            this.wallServiceClient = new wall_service_client_1.WallServiceClient({
                wallId: this.opts.wallId,
                connectionId: this.opts.connectionId,
                refreshToken: this.opts.refreshToken,
                itemEntryEventHandler: this.handleWallEvent.bind(this),
                connectionErrorHandler: this.handleConnectionError.bind(this),
                initialToken: this.opts.initialCreds.token,
                reconnectionHandler: this.handleReconnect.bind(this),
            });
            const loadWallItemsPromise = this.loadExistingWallItemEvents();
            const connectPromise = this.wallServiceClient.connect();
            const [_, connected] = yield Promise.all([loadWallItemsPromise, connectPromise]);
            if (!connected) {
                logger_1.logger.error('Failed to connect to wall service', {
                    wallId: this.opts.wallId,
                    connectinId: this.opts.connectionId,
                });
            }
            else {
                this.connectedSubject.next(true);
            }
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.isSyncConnected()) {
                logger_1.logger.warn(`Unexpected state: disconnect called when wallId ${this.opts.wallId} was already disconnected`);
                return;
            }
            logger_1.logger.info(`StreemWallSync shutting down`);
            this.wallServiceClient.disconnect();
            this.eventSubject.complete();
            this.opts.onDisconnect();
            this.connectedSubject.next(false);
        });
    }
    add(item) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.wallServiceClient, 'StreemWallSync.connect was not called');
            const wallItem = this.encodeWallItem(item);
            try {
                yield this.wallServiceClient.addOrUpdate(wallItem, item.id);
                // don't spam logs on heartbeat.
                const level = item.type === 'heartbeat' ? 'debug' : 'info';
                logger_1.logger[level](`WallServiceClient UPDATED WallItem wallItemType=${item.type} wallItemId=${item.id} with connectionId=${this.opts.connectionId}`);
            }
            catch (error) {
                logger_1.logger.error(`WallServiceClient FAILED to update WallItem wallItemId=${item.id} with connectionId=${this.opts.connectionId}`, error);
            }
        });
    }
    update(itemId, item) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.wallServiceClient, 'StreemWallSync.connect was not called');
            const wallItem = this.encodeWallItem(item);
            try {
                yield this.wallServiceClient.addOrUpdate(wallItem, itemId);
                // don't spam logs on heartbeat.
                const level = item.type === 'heartbeat' ? 'debug' : 'info';
                logger_1.logger[level](`WallServiceClient UPDATED WallItem wallItemType=${item.type} wallItemId=${item.id} with connectionId=${this.opts.connectionId}`);
            }
            catch (error) {
                logger_1.logger.error(`WallServiceClient FAILED to update WallItem wallItemId=${item.id} with connectionId=${this.opts.connectionId}`, error);
            }
        });
    }
    delete(itemId) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(this.wallServiceClient, 'StreemWallSync.connect was not called');
            try {
                yield this.wallServiceClient.delete(itemId);
                logger_1.logger.info(`StreemWallSync DELETED wallItemId=${itemId}`);
                return true;
            }
            catch (error) {
                logger_1.logger.error(`StreemWallSync Failed to delete item with id: ${itemId} from StreemWallSync with connectionId=${this.opts.connectionId}`, error);
                return false;
            }
        });
    }
    convertToWallItemEventType(type) {
        if (type === 'EVENT_TYPE_ADDED') {
            return 'ADDED';
        }
        else if (type === 'EVENT_TYPE_DELETED') {
            return 'DELETED';
        }
        else if (type === 'EVENT_TYPE_UPDATED') {
            return 'UPDATED';
        }
        throw new Error(`Unknown event type: ${type}`);
    }
    isEventLocal({ connection_id: connectionId }) {
        return connectionId === this.opts.connectionId;
    }
    handleWallEvent(event) {
        // need to ignore "local" events as they will already be present in the wall
        //    and republishing here would cause duplicates
        if (this.isEventLocal(event)) {
            return;
        }
        const { wall_item_encoded: wallItemEncoded } = event.wall_item_entry;
        const decodedWallItem = this.decodeWallItem(wallItemEncoded);
        const wallItemEvent = {
            type: this.convertToWallItemEventType(event.event_type),
            itemId: decodedWallItem.id,
            itemType: decodedWallItem.type,
            item: decodedWallItem,
        };
        this.eventSubject.next(wallItemEvent);
    }
    handleConnectionError(err) {
        // This is called after retries are exhausted in the wall_service_client, so
        //    reconnect is not called here.
        //
        //    This matches the current behavior of twilio_wall_sync.ts, although this
        //    doesn't seem like good UX.  Perhaps this should be a follow up story
        //    that applies to both of the wall syncs?
        logger_1.logger.error('handleConnectionError', err);
    }
    decodeWallItem(encodedWallItem) {
        const buffer = minimal_1.util.newBuffer(minimal_1.util.base64.length(encodedWallItem));
        minimal_1.util.base64.decode(encodedWallItem, buffer, 0);
        return this.wallItemDecoder.decode(buffer);
    }
    loadExistingWallItemEvents() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallEntries = yield this.wallServiceClient.getEntriesForWall();
            wallEntries.forEach(entry => {
                const encodedWallItem = entry.wall_item_encoded;
                if (typeof encodedWallItem !== 'string') {
                    logger_1.logger.warn('StreemSyncClient encountered an invalid Wall Item', entry);
                    return;
                }
                const wallItem = this.decodeWallItem(encodedWallItem);
                const event = {
                    type: 'ADDED',
                    itemId: wallItem.id,
                    itemType: wallItem.type,
                    item: wallItem,
                };
                this.eventSubject.next(event);
            });
        });
    }
    handleReconnect(reconnectionSuccessful) {
        return __awaiter(this, void 0, void 0, function* () {
            if (reconnectionSuccessful) {
                // rebroadcast all events from the wall service to the wall in case any were
                //       missed during the disconnect
                yield this.loadExistingWallItemEvents();
            }
            else {
                logger_1.logger.error('Connection lost to wall service lost.  Unable to reconnect.', {
                    wallId: this.opts.wallId,
                    connectinId: this.opts.connectionId,
                });
                this.reconnectFailedSubject.next(true);
            }
        });
    }
}
exports.StreemWallSync = StreemWallSync;
//# sourceMappingURL=streem_wall_sync.js.map